import { Link, useRouteError } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { Button, EmptyState, PageSpinner } from '@/ui';

const RELOADED_AT = 'manhattan.reloadedForUpdate';

/**
 * A page from an older build asking for a code chunk the latest deploy replaced: the browser
 * reports it as a failed dynamic import (wording differs per browser).
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(message);
}

/**
 * Loads the new build after a deploy. At most once a minute, so a real outage shows the error
 * page instead of reloading forever. Returns false when it did not reload.
 */
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT));
    if (last && Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    /* storage unavailable: reload anyway */
  }
  window.location.reload();
  return true;
}

/** Route error screen: reloads into the new version after a deploy, else a plain way out. */
export function RouteError() {
  const { t } = useTranslation('shell');
  const error = useRouteError();
  if (isStaleChunkError(error) && reloadForNewVersion()) return <PageSpinner label={t('errors.updating')} />;
  return (
    <EmptyState
      size="page"
      icon={<TriangleAlert />}
      title={t('errors.unexpected')}
      description={t('errors.unexpectedDescription')}
      action={
        <span className="flex flex-wrap justify-center gap-2">
          <Button variant="primary" icon={<RefreshCw />} onClick={() => window.location.reload()}>
            {t('errors.reload')}
          </Button>
          <Button asChild>
            <Link to="/">{t('errors.goHome')}</Link>
          </Button>
        </span>
      }
    />
  );
}
